import React, { useEffect, useRef } from 'react';
import { Home, User, Briefcase, Mail, Code, Terminal } from 'lucide-react';
import anime from 'animejs';
import animationManager from '../utils/AnimationManager';

/**
 * NavigationDock - Floating navigation with glassmorphism
 * Animated icon buttons for quick access
 */
const NavigationDock = ({ onCommandSelect }) => {
  const dockRef = useRef(null);

  const navItems = [
    { icon: Home, label: 'Home', command: 'clear' },
    { icon: User, label: 'About', command: 'whoami' },
    { icon: Briefcase, label: 'Projects', command: 'projects' },
    { icon: Code, label: 'Skills', command: 'skills' },
    { icon: Mail, label: 'Contact', command: 'contact' },
    { icon: Terminal, label: 'System', command: 'neofetch' }
  ];

  useEffect(() => {
    if (!dockRef.current || !animationManager.shouldAnimate()) return;

    // Entrance animation
    const timeline = animationManager.createTimeline('nav-dock');
    timeline.add({
      targets: dockRef.current,
      scale: [0, 1],
      opacity: [0, 1],
      duration: 600,
      easing: 'easeOutElastic(1, .6)'
    });

    // Stagger icon animations
    const icons = dockRef.current.querySelectorAll('.nav-icon');
    timeline.add({
      targets: icons,
      rotateZ: [360, 0],
      scale: [0, 1],
      delay: anime.stagger(100),
      duration: 500,
      easing: 'easeOutElastic(1, .8)'
    }, '-=400');

    return () => animationManager.removeTimeline('nav-dock');
  }, []);

  const handleIconHover = (e) => {
    if (!animationManager.shouldAnimate()) return;
    
    anime({
      targets: e.currentTarget,
      scale: 1.2,
      rotate: 5,
      duration: 300,
      easing: 'easeOutCubic'
    });
  };

  const handleIconLeave = (e) => {
    if (!animationManager.shouldAnimate()) return;
    
    anime({
      targets: e.currentTarget,
      scale: 1,
      rotate: 0,
      duration: 300,
      easing: 'easeOutCubic'
    });
  };

  const handleIconClick = (e, command) => {
    if (animationManager.shouldAnimate()) {
      // Ripple effect
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      animationManager.ripple(x, y, e.currentTarget);

      // Click animation
      anime({
        targets: e.currentTarget,
        scale: [1, 0.9, 1],
        duration: 300,
        easing: 'easeOutCubic'
      });
    }

    onCommandSelect(command);
  };

  return (
    <div
      ref={dockRef}
      className="fixed bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-50 opacity-0"
      style={{ fontFamily: "'Orbitron', sans-serif" }}
    >
      <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 px-2 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 rounded-xl sm:rounded-2xl backdrop-blur-md bg-[#0a0a2a]/60 border border-[#00ff41]/30 shadow-2xl">
        {navItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              className="nav-icon relative group p-2 sm:p-2.5 md:p-3 rounded-lg sm:rounded-xl bg-[#0a0a2a]/50 border border-[#00ff41]/20 hover:border-[#00ff41]/60 transition-all duration-300 overflow-hidden active:scale-95"
              onMouseEnter={handleIconHover}
              onMouseLeave={handleIconLeave}
              onClick={(e) => handleIconClick(e, item.command)}
              title={item.label}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#00ff41]" />
              
              {/* Tooltip */}
              <div className="absolute -top-8 sm:-top-10 left-1/2 transform -translate-x-1/2 px-2 sm:px-3 py-1 bg-[#0a0a2a] border border-[#00ff41]/50 rounded-lg text-[10px] sm:text-xs text-[#00ff41] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                {item.label}
              </div>

              {/* Glow effect */}
              <div className="absolute inset-0 bg-[#00ff41]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NavigationDock;
